import React from "react";
import MuiAlert, { type AlertProps } from "@mui/material/Alert";
import { useTheme } from "@mui/material/styles";
import { designTokens, interfaceFont } from "../design/tokens.js";
import dico from "../../../util/src/language/dico.js";
import { useInterfaceDesign } from "../design/InterfaceDesign.js";

export function useFeedbackTokens() {
  const theme = useTheme();
  const preference = useInterfaceDesign();
  return designTokens[
    preference.mode === "modern" ? preference.colorMode : theme.palette.mode
  ];
}

/** Drop-in Alert: caller content, severity, actions and custom icons stay intact. */
export const FeedbackAlert = React.forwardRef<HTMLDivElement, AlertProps>(
  function FeedbackAlert({ severity = "success", sx, role, ...props }, ref) {
    const p = useFeedbackTokens();
    const color = {
      success: p.success,
      info: p.accent,
      warning: p.warning,
      error: p.error,
    }[severity];
    return (
      <MuiAlert
        {...props}
        ref={ref}
        severity={severity}
        closeText={props.closeText ?? dico().close}
        role={role ?? (severity === "error" ? "alert" : "status")}
        sx={[
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
          {
            boxSizing: "border-box",
            maxWidth: "100%",
            minWidth: 0,
            fontFamily: interfaceFont,
            fontSize: 12,
            lineHeight: 1.5,
            color: p.text,
            backgroundColor: `color-mix(in srgb, ${color} 7%, ${p.surface})`,
            backgroundImage: "none",
            border: `1px solid ${p.border}`,
            borderRadius: "8px",
            boxShadow: "none",
            padding: "12px 14px",
            gap: "11px",
            alignItems: "flex-start",
            overflowWrap: "anywhere",
            "& .MuiAlert-icon": {
              width: 28,
              height: 28,
              borderRadius: "8px",
              backgroundColor: color,
              color: p.surface,
              opacity: 1,
              margin: 0,
              padding: 0,
              flexShrink: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              "& .MuiSvgIcon-root": { fontSize: 18 },
              "& .MuiCircularProgress-root": { color: p.surface },
            },
            "& .MuiAlert-message": { padding: "3px 0", minWidth: 0, flex: 1 },
            "& .MuiAlertTitle-root": {
              color: p.text,
              fontSize: 12,
              fontWeight: 650,
              lineHeight: 1.5,
              margin: "0 0 3px",
            },
            "& .MuiTypography-root": { color: "inherit" },
            "& .MuiAlert-action": {
              gap: "7px",
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              padding: 0,
              margin: 0,
            },
            "& .MuiButton-root": {
              fontFamily: interfaceFont,
              fontSize: 12,
              lineHeight: 1.5,
              textTransform: "none",
              minWidth: 0,
              padding: "5px 10px",
              borderRadius: "6px",
              border: `1px solid ${p.border}`,
              backgroundColor: p.surface,
              color: p.text,
              boxShadow: "none",
              "&:hover": { backgroundColor: p.chrome, boxShadow: "none" },
            },
            "& .MuiIconButton-root": { color: p.muted, padding: "4px" },
            "@media (max-width: 600px)": {
              flexWrap: "wrap",
              "& .MuiAlert-action": { marginLeft: "39px", flexBasis: "100%" },
            },
            "&[data-feedback-toast]": {
              backgroundColor: p.surface,
              borderRadius: "10px",
              width: "100%",
              minWidth: 0,
            },
          },
        ]}
      />
    );
  },
);
